/* eslint-disable camelcase */
// As propriedades dos eventos Mixpanel são snake_case por convenção do schema
// de analytics (e para casar com o catálogo do plano), não camelCase.
import {
  track,
  registrarSuperPropriedades,
  limparSuperPropriedades,
  identificar,
  iniciarCronometro
} from './mixpanel'

/**
 * Catálogo tipado dos eventos Mixpanel do fluxo de matrícula da pós.
 *
 * Convenção: `matricula_<objeto>_<acao>` (snake_case).
 * IMPORTANTE (LGPD): nunca enviar valores de campos pessoais, do responsável
 * financeiro (terceiro) ou do cartão. Eventos de campo enviam só QUE/QUAL campo,
 * nunca o conteúdo.
 */

type Etapa =
  | 'DadosPessoais'
  | 'DadosPlanoPagamento'
  | 'TermosECondicoes'
  | 'DadosPagamento'

const EVENTO_CONCLUIDA = 'matricula_concluida'

// Super properties — acompanham todos os eventos da sessão do wizard.
const CHAVES_SUPER = [
  'tipo_matricula',
  'modo_matricula',
  'curso_nome',
  'turma_codigo',
  'turma_split',
  'matricula_id'
]

export interface SuperPropriedadesMatricula {
  tipo_matricula: string
  modo_matricula: string
  curso_nome?: string
  turma_codigo?: string
  turma_split?: boolean
  [chave: string]: unknown
}

// Estado por sessão do wizard (zerado ao limpar contexto / sair do fluxo):
// - visitasPorEtapa: nº de vezes que cada etapa foi iniciada (detecta revisitas
//   quando o aluno volta um passo).
// - ultimoValorPorEvento: dedup de eventos disparados por re-render (cupom,
//   responsável financeiro, plano) — só emite quando o valor muda de fato.
const visitasPorEtapa: Record<string, number> = {}
const ultimoValorPorEvento: Record<string, string> = {}

const emitirSeMudou = (
  chave: string,
  valor: string,
  emitir: () => void
): void => {
  if (ultimoValorPorEvento[chave] === valor) return
  ultimoValorPorEvento[chave] = valor
  emitir()
}

const resetarEstadoSessao = (): void => {
  Object.keys(visitasPorEtapa).forEach(k => delete visitasPorEtapa[k])
  Object.keys(ultimoValorPorEvento).forEach(k => delete ultimoValorPorEvento[k])
}

export const trackMatricula = {
  // ---- Super properties / identidade ----
  registrarContexto: (props: SuperPropriedadesMatricula): void =>
    registrarSuperPropriedades(props),

  registrarMatriculaId: (matriculaId: string): void =>
    registrarSuperPropriedades({ matricula_id: matriculaId }),

  limparContexto: (): void => {
    limparSuperPropriedades(CHAVES_SUPER)
    resetarEstadoSessao()
  },

  iniciarCronometroConclusao: (): void => iniciarCronometro(EVENTO_CONCLUIDA),

  identificar: (idUsuario: string): void => identificar(idUsuario),

  // ---- Topo do funil ----
  // Dispara 1× por sessão: se o aluno loga no meio, o wizard recarrega e
  // chamaria de novo — mas é a MESMA abertura do link, não conta 2×.
  paginaAberta: (usuarioAutenticado: boolean): void =>
    emitirSeMudou('pagina_aberta', 'aberta', () =>
      track('matricula_pagina_aberta', {
        usuario_autenticado: usuarioAutenticado
      })
    ),

  paginaBloqueada: (
    motivo:
      | 'ja_matriculado'
      | 'pessoa_juridica'
      | 'periodo_invalido'
      | 'erro_carregamento',
    usuarioAutenticado: boolean
  ): void =>
    track('matricula_pagina_bloqueada', {
      motivo,
      usuario_autenticado: usuarioAutenticado
    }),

  wizardIniciado: (props: {
    usuario_autenticado: boolean
    retomada: boolean
    etapa_retomada?: string | null
    turma_situacao?: string
  }): void => track('matricula_wizard_iniciado', props),

  identificacao: (metodo: 'cadastro' | 'login'): void =>
    track('matricula_identificacao', { metodo }),

  // ---- Funil por passo (base da medição de desistência) ----
  // Conta visitas: ao reentrar numa etapa (aluno voltou um passo), marca
  // revisita=true e visita_etapa=N. O Funnel do Mixpanel conta pessoa única
  // (não infla), e ganhamos o sinal de re-tentativa.
  etapaIniciada: (etapa: Etapa, extras?: Record<string, unknown>): void => {
    visitasPorEtapa[etapa] = (visitasPorEtapa[etapa] ?? 0) + 1
    track('matricula_etapa_iniciada', {
      etapa,
      visita_etapa: visitasPorEtapa[etapa],
      revisita: visitasPorEtapa[etapa] > 1,
      ...extras
    })
  },

  etapaConcluida: (etapa: Etapa): void =>
    track('matricula_etapa_concluida', { etapa }),

  // ---- Fricção (validação Yup) ----
  validacaoErro: (etapa: Etapa, erros: Record<string, string>): void =>
    Object.entries(erros ?? {}).forEach(([campo, motivo]) =>
      track('matricula_validacao_erro', { etapa, campo, motivo })
    ),

  // ---- Abandono (best-effort, só refina) ----
  etapaAbandonada: (props: {
    etapa: string
    ultimo_campo_focado?: string | null
    forma_selecionada?: string | null
  }): void => track('matricula_etapa_abandonada', props),

  // ---- Passo 1 — Dados Pessoais ----
  dadosPessoaisAvancarClicado: (): void =>
    track('matricula_dados_pessoais_avancar_clicado'),

  dadosPessoaisConcluido: (props: {
    nacionalidade?: string
    possui_nome_social?: boolean
  }): void => track('matricula_dados_pessoais_concluido', props),

  dadosPessoaisErro: (props: {
    status_http?: number
    motivo?: string
  }): void => track('matricula_dados_pessoais_erro', props),

  // ---- Passo 2 — Plano de Pagamento (sub-funil de marcos) ----
  formaPagamentoSelecionada: (forma: string): void =>
    track('matricula_forma_pagamento_selecionada', { forma }),

  planosCarregados: (forma: string, quantidade: number): void =>
    track('matricula_planos_carregados', {
      forma,
      quantidade_planos: quantidade,
      lista_vazia: quantidade === 0
    }),

  planosErro: (forma: string, statusHttp?: number): void =>
    track('matricula_planos_erro', { forma, status_http: statusHttp }),

  planoSelecionado: (props: {
    forma?: string
    parcelas?: number
    valor_parcela?: number
    plano_base?: boolean
  }): void =>
    emitirSeMudou(
      'plano_selecionado',
      `${props.forma}-${props.parcelas}-${props.valor_parcela}`,
      () => track('matricula_plano_selecionado', props)
    ),

  diaVencimentoSelecionado: (dia: number | string): void =>
    track('matricula_dia_vencimento_selecionado', { dia }),

  cupomMarcado: (marcado: boolean): void =>
    track('matricula_cupom_marcado', { marcado }),

  cupomPreenchido: (codigo?: string): void =>
    emitirSeMudou('cupom_preenchido', codigo ?? '', () =>
      track('matricula_cupom_preenchido')
    ),

  cupomAplicado: (
    props: {
      tipo_desconto?: string
      valor_desconto?: number
    },
    codigo?: string
  ): void =>
    emitirSeMudou('cupom_resultado', `ok-${codigo ?? ''}`, () =>
      track('matricula_cupom_aplicado', props)
    ),

  cupomRejeitado: (
    motivo:
      | 'expirado'
      | 'limite_atingido'
      | 'forma_invalida'
      | 'nao_encontrado',
    codigo?: string
  ): void =>
    emitirSeMudou('cupom_resultado', `erro-${codigo ?? ''}-${motivo}`, () =>
      track('matricula_cupom_rejeitado', { motivo })
    ),

  responsavelFinanceiroMarcado: (possui: boolean): void =>
    track('matricula_responsavel_financeiro_marcado', { possui }),

  rfTipoSelecionado: (tipo: 'PF' | 'PJ'): void =>
    emitirSeMudou('rf_tipo_selecionado', tipo, () =>
      track('matricula_rf_tipo_selecionado', { tipo })
    ),

  rfEnderecoDiferente: (diferente: boolean): void =>
    track('matricula_rf_endereco_diferente', { diferente }),

  planoPagamentoAvancarClicado: (): void =>
    track('matricula_plano_pagamento_avancar_clicado'),

  planoPagamentoConcluido: (props: {
    forma?: string
    parcelas?: number
    tem_responsavel_financeiro?: boolean
    tem_cupom?: boolean
    dia_vencimento?: number | string
  }): void => track('matricula_plano_pagamento_concluido', props),

  planoPagamentoErro: (props: {
    status_http?: number
    motivo?: string
  }): void => track('matricula_plano_pagamento_erro', props),

  // ---- Passo 3 — Termos ----
  contratoAberto: (): void => track('matricula_contrato_aberto'),
  contratoCarregado: (): void => track('matricula_contrato_carregado'),

  termoMarcado: (
    campo: 'liAceitoTermos' | 'estouCiente',
    marcado: boolean
  ): void => track('matricula_termo_marcado', { campo, marcado }),

  termosAvancarClicado: (): void => track('matricula_termos_avancar_clicado'),
  termosAceitos: (): void => track('matricula_termos_aceitos'),
  termosErro: (statusHttp?: number): void =>
    track('matricula_termos_erro', { status_http: statusHttp }),

  // ---- Passo 4 — Pagamento ----
  pagamentoConcluirClicado: (forma: string): void =>
    track('matricula_pagamento_concluir_clicado', { forma }),

  pagamentoSubmetido: (props: {
    forma: string
    bandeira?: string | null
    parcelas_cartao?: number | null
  }): void => track('matricula_pagamento_submetido', props),

  pagamentoConcluido: (props: {
    forma?: string
    situacao_matricula?: string
    gateway?: string
    recorrente?: boolean
    taxa_isenta?: boolean
  }): void => track('matricula_pagamento_concluido', props),

  pagamentoErro: (props: {
    forma?: string
    gateway?: string
    status_http?: number
    codigo_retorno_gateway?: string | null
    motivo?: string
  }): void => track('matricula_pagamento_erro', props),

  boletoGerado: (forma: string): void =>
    track('matricula_boleto_gerado', { forma }),

  pixQrcodeExibido: (): void => track('matricula_pix_qrcode_exibido'),

  // ---- Conclusão ----
  concluida: (props: {
    forma?: string
    situacao_final?: string
    acesso_imediato?: boolean
  }): void => track(EVENTO_CONCLUIDA, props)
}
